/**
 * Site health in the person's words: one sentence per site, a word per area, and
 * the notes a card shows once acted on (designer's notes "Site Health (P2, P5, P6,
 * P7)"; app/Site Health Concept). The server sends statuses and suggestions, never
 * a check, a threshold or a count of checks, so nothing here can show one.
 */

export type HealthStatus = 'good' | 'improve' | 'fix';
export type HealthArea = 'safe' | 'search' | 'speed' | 'ai' | 'signed';
export type HealthAction = 'agent' | 'review' | 'keep+agent';

export interface HealthItem {
  id: string;
  kind: 'page' | 'article' | 'siteDoc' | 'post';
  title: string;
}

export interface HealthDone {
  action: 'agent' | 'review' | 'keep';
  at: string;
  byYou: boolean;
  by: string | null;
  reason?: string;
}

export interface HealthSuggestion {
  id: string;
  area: HealthArea;
  severity: 'fix' | 'improve';
  title: string;
  why: string;
  items: HealthItem[];
  itemsLabel?: string;
  action: HealthAction;
  cta: string;
  agentPrompt?: string;
  done?: HealthDone;
  /** The fix is a place in the app, not a job for the agent: the button goes there. */
  to?: string;
}

export interface HealthHistoryRow {
  runAt: string;
  overall: HealthStatus;
  areas: Record<HealthArea, HealthStatus>;
}

export interface SiteHealthView {
  state: 'first' | 'empty' | 'ready';
  runAt?: string;
  trigger?: 'nightly' | 'checkNow';
  overall?: HealthStatus;
  areas?: Record<HealthArea, HealthStatus>;
  suggestions?: HealthSuggestion[];
  signedOff?: { approved: number; live: number };
  fixedBeforeReview30d?: number;
  realLoading?: { summary: string };
  history?: HealthHistoryRow[];
  features: { history: boolean; realLoading: boolean; socialPosts: boolean; checkNow: boolean };
  canKeep: boolean;
  checking: boolean;
  canCheckNow: boolean;
  gscConnected: boolean;
  /** The latest check didn't finish: Check now's, or last night's. */
  checkFailed?: 'checkNow' | 'nightly' | null;
  siteLocked?: boolean;
}

/** In the design's order, and AI assistants marked new until it has proved itself. */
export const AREAS: Array<{ key: HealthArea; name: string; short: string; isNew?: boolean }> = [
  { key: 'safe', name: 'Safe for visitors', short: 'Safe' },
  { key: 'search', name: 'Found in search', short: 'Search' },
  { key: 'speed', name: 'Loads quickly', short: 'Speed' },
  { key: 'ai', name: 'Read well by AI assistants', short: 'AI', isNew: true },
  { key: 'signed', name: 'Signed off', short: 'Signed off' },
];

export const areaName = (key: HealthArea) => AREAS.find((a) => a.key === key)?.name || key;

export const STATUS_WORD: Record<HealthStatus, string> = {
  fix: 'Needs a fix',
  improve: 'Room to improve',
  good: 'Good',
};

/**
 * "{n} article addresses have a stray number" (Round 44 §5). Read live, not from
 * last night's check: fixing them is one click, so it has to clear at once.
 */
export function withAddressItem(view: SiteHealthView, n: number): SiteHealthView {
  if (view.state !== 'ready' || !n) return view;
  const worse = (s?: HealthStatus): HealthStatus => (s === 'fix' ? 'fix' : 'improve');
  return {
    ...view,
    overall: worse(view.overall),
    areas: view.areas ? { ...view.areas, search: worse(view.areas.search) } : view.areas,
    suggestions: [
      {
        id: 'article-addresses',
        area: 'search',
        severity: 'improve',
        title: `${n.toLocaleString('en-US')} article ${n === 1 ? 'address has' : 'addresses have'} a stray number`,
        why: 'A curly quote or dash in the title left it there. One click fixes them all, and the old addresses redirect.',
        items: [],
        action: 'review',
        cta: 'Fix in Article settings',
        to: '/content/settings#addresses',
      },
      ...(view.suggestions || []),
    ],
  };
}

export const openSuggestions = (view: SiteHealthView) =>
  (view.suggestions || []).filter((s) => !s.done);

/** The sentence, set by the worst thing that's open. AI assistants never sets it. */
export function healthHeadline(view: SiteHealthView): string {
  if (view.state === 'first') return 'Your first check runs tonight.';
  if (view.state === 'empty') return 'Nothing live yet.';
  const fixes = openSuggestions(view).filter((s) => s.severity === 'fix' && s.area !== 'ai').length;
  if (fixes)
    return fixes === 1 ? 'One thing needs your attention.' : `${fixes} things need your attention.`;
  return view.overall === 'improve' ? 'Looking good. A few suggestions.' : 'Looking good.';
}

/** The line under it. */
export function healthSubLine(view: SiteHealthView): string {
  if (view.state === 'first') return 'We check every night.';
  if (view.state === 'empty') return 'We’ll check your site once something goes live.';
  const open = openSuggestions(view);
  const fixes = open.filter((s) => s.severity === 'fix' && s.area !== 'ai').length;
  const n = open.length - fixes;
  if (!n) return 'Nothing else to do';
  const words = `${n} ${n === 1 ? 'suggestion' : 'suggestions'}`;
  return fixes ? `Plus ${words}` : words;
}

const monthDay = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '';

/** When the last result is from, in the words a person uses. */
function resultFrom(view: SiteHealthView, now: Date): string {
  if (!view.runAt) return 'From last night';
  const at = new Date(view.runAt);
  if (view.trigger === 'checkNow' && at.toDateString() === now.toDateString()) return 'From today';
  // The nightly check runs after midnight, so "today" on the calendar is last night.
  return now.getTime() - at.getTime() < 36 * 3600000
    ? 'From last night'
    : `From ${monthDay(view.runAt)}`;
}

export type TodayHealthDot = HealthStatus | 'none' | 'checking' | 'failed';

/**
 * Today's Site health column in every state (app/Today Site Health Column): when
 * it was checked, the sentence, what to do. A snapshot, never a trend. The worst
 * open finding leads, and a result being replaced or out of date is dimmed.
 */
export function todayHealth(view: SiteHealthView, now = new Date()) {
  const open = [...openSuggestions(view)].sort(
    (a, b) => Number(b.severity === 'fix') - Number(a.severity === 'fix')
  );
  const ready = view.state === 'ready';
  const base = {
    when: !ready
      ? 'Not checked yet'
      : view.trigger === 'checkNow'
        ? 'Checked today'
        : 'Checked last night',
    dot: (ready ? view.overall || 'good' : 'none') as TodayHealthDot,
    pulse: false,
    dim: false,
    /** Nothing checked yet: hollow dots and nothing to click. */
    hollow: !ready,
    headline: healthHeadline(view),
    sub: !open.length && ready ? 'Nothing to do' : healthSubLine(view),
    action: null as 'check' | 'retry' | null,
    link: null as { label: string; to: string } | null,
    open,
    rows: open.slice(0, 3),
    fixed: ready && !open.length ? view.fixedBeforeReview30d || 0 : 0,
  };
  if (view.state === 'first') {
    return view.checking
      ? {
          ...base,
          when: 'Checking now',
          dot: 'checking' as const,
          pulse: true,
          headline: 'Checking your site for the first time.',
          sub: 'This updates when it’s done. You can leave this page.',
        }
      : {
          ...base,
          sub: 'We’ll look at safety, search, speed and AI assistants, then list anything to fix here.',
          action: view.canCheckNow ? ('check' as const) : null,
        };
  }
  if (!ready) return base;
  if (view.checking) {
    return {
      ...base,
      when: 'Checking now',
      dot: 'checking' as const,
      pulse: true,
      dim: true,
      sub: `${resultFrom(view, now)}. Checking again now. This updates when it’s done.`,
    };
  }
  if (view.checkFailed) {
    const failed = {
      ...base,
      when: `Last checked ${monthDay(view.runAt)}`,
      dot: 'failed' as const,
      dim: true,
    };
    return view.siteLocked
      ? {
          ...failed,
          headline: 'We couldn’t reach your site.',
          sub: `It’s password protected, so the check can’t see your pages. Showing results from ${monthDay(view.runAt)}.`,
          link: {
            label: 'Change who can see your site →',
            to: '/content?view=manage&area=site',
          },
        }
      : {
          ...failed,
          headline:
            view.checkFailed === 'checkNow'
              ? 'That check didn’t finish.'
              : 'Last night’s check didn’t finish.',
          sub: `Showing results from ${monthDay(view.runAt)}. We’ll try again tonight.`,
          action: view.canCheckNow ? ('retry' as const) : null,
        };
  }
  return base;
}

/** The status block's note. Never how long a check takes. */
export function checkedNote(view: SiteHealthView): string | null {
  if (view.checking) return 'Checking now. This page updates when it’s done.';
  if (view.state !== 'ready') return null;
  return view.trigger === 'checkNow' ? 'Checked today' : 'Checked last night';
}

/** What the area's row says it covers, for this site's plan. */
export function areaDescription(key: HealthArea, view: SiteHealthView): string {
  const social = view.features.socialPosts ? ' Includes social posts.' : '';
  switch (key) {
    case 'safe':
      return `Nothing on your site puts visitors or your accounts at risk.${social}`;
    case 'search':
      return 'Search engines can find your pages, understand them and show them well.';
    case 'speed':
      return 'Pages open fast, even on a phone.';
    case 'ai':
      return 'AI assistants can read your pages and quote them correctly.';
    default: {
      const s = view.signedOff;
      return s
        ? `${s.approved} of ${s.live} live items have a recorded approver.${social}`
        : `Live items have a recorded approver.${social}`;
    }
  }
}

/**
 * Loads quickly's extra line: real visitors' numbers on Business and Pro. Nothing
 * when they're on a plan with the numbers and connected, but Google has none yet.
 */
export function speedExtra(view: SiteHealthView): string | null {
  if (!view.features.realLoading) {
    return 'Real loading times from Google Search Console are on Business and Pro.';
  }
  if (!view.gscConnected) return 'Connect Google Search Console to see real loading times.';
  return view.realLoading?.summary || null;
}

const SECOND = 1000;
const JUST_NOW = 120 * SECOND;

/** A card's note once acted on. */
export function doneNote(done: HealthDone, now = Date.now()): string {
  if (done.action === 'agent')
    return 'Sent to your agent. Its fix will come back for your approval.';
  const who = done.byYou ? 'you' : done.by || 'a teammate';
  if (done.action === 'keep') return `Kept by ${who}. Reason recorded: ${done.reason || ''}`.trim();
  const recent = now - new Date(done.at).getTime() < JUST_NOW;
  return `Signed off by ${who}${recent ? ', just now' : ''}.`;
}

/** Where an item is opened, so a fix can be looked at on the owner's own page. */
export function itemLink(item: HealthItem): string {
  if (item.kind === 'article') return `/content/${item.id}`;
  if (item.kind === 'page') return `/content?view=pages&page=${item.id}`;
  if (item.kind === 'post') return '/reach';
  return '/content?view=manage&area=design';
}

/** First 5, then "Show {n} more". */
export const SUGGESTIONS_SHOWN = 5;

/** Open ones first, then what's been acted on; a filter narrows to one area. */
export function visibleSuggestions(
  all: HealthSuggestion[],
  area: HealthArea | null
): HealthSuggestion[] {
  const list = area ? all.filter((s) => s.area === area) : all;
  return [...list].sort((a, b) => Number(Boolean(a.done)) - Number(Boolean(b.done)));
}

export function historyDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
